<template>
    <ModalWrapper v-model="modal" @close="modal = false">
        <template v-slot:title>
            {{ t(!selectedRate ? 'create_rate' : 'edit_rate') }}
        </template>
        <template v-slot:content>
            <v-form v-model="valid">
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="rateObj.baseCurrency"
                            :items="currencyList"
                            :label="t('base_currency')"
                            density="compact"
                            color="primary"
                            disabled
                            data-cy="base-currency-select"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="rateObj.currencyCode"
                            :items="currencyList"
                            :label="t('currency')"
                            :rules="[requiredRule('currency')]"
                            density="compact"
                            color="primary"
                            data-cy="currency-code-select"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="rateObj.factor"
                            :items="factorList"
                            :label="t('factor')"
                            :rules="[requiredRule('factor')]"
                            density="compact"
                            color="primary"
                            data-cy="factor-select"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="rateObj.rate"
                            :label="t('rate')"
                            :rules="[requiredRule('rate')]"
                            type="number"
                            density="compact"
                            color="primary"
                            data-cy="rate-field"
                            class="input--no-arrows"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </template>
        <template v-slot:btns>
            <v-btn
                :loading="isLoading"
                :disabled="isLoading || !valid"
                variant="flat"
                data-cy="submit-btn"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>

<script setup lang="ts">
    const props = defineProps<{
        selectedRate: any
    }>()
    const modal = defineModel<boolean>({
        default: false,
    })
    const emit = defineEmits<{
        (e: 'update'): void
    }>()

    const app = useNuxtApp()
    const conf = useRuntimeConfig()
    const {t} = useI18n()

    const currencyList: ComputedRef<any[]> = computed(() => Object.values(conf.public.currency.available))

    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const rateObj = ref<{baseCurrency: string; currencyCode: string; factor: number; rate: number}>(initRateData())
    const factorList = ref<number[]>([1, 10, 100, 1000, 10000, 100000, 10000000, 10000000, 100000000])

    watch(
        () => modal.value,
        () => {
            rateObj.value = initRateData()

            if (props.selectedRate?.currencyCode) {
                rateObj.value = props.selectedRate
            } else rateObj.value.baseCurrency = 'USD'
        }
    )

    async function save(): Promise<void> {
        try {
            isLoading.value = true

            await app.$api.manualExchangeRates.post({
                currencyCode: rateObj.value.currencyCode,
                factor: rateObj.value.factor,
                rate: rateObj.value.rate,
            })

            modal.value = false
            emit('update')
        } catch (e) {
            console.error(e)
        } finally {
            isLoading.value = false
        }
    }

    // TODO: duplicate from FormValidationRulesMixin
    function requiredRule(prop): any {
        return v => !!v || v === 0 || `${t(prop)} ${t('validation.required')}`
    }

    function initRateData(): any {
        return {
            baseCurrency: null,
            currencyCode: null,
            factor: null,
            rate: null,
        }
    }
</script>
